import {
  formatError,
  login,
  runLogoutTimer,
  signUp,
  sendPasswordReset,
  verifyResetCode,
  confirmPasswordResetWithCode,
  checkUserSignInMethods,
} from '../../services/AuthService';

import axios from 'axios';
import swal from 'sweetalert';
import { clearBrowserIdentity, markBrowserSession, withBrowserSessionLock, sessionMarker, sessionChangedError } from '../../services/BrowserSessionService';
import { safeAuthRedirect, rememberInvitation } from '../../utils/OnboardingRedirect';
import { requestEmailVerification, verificationFailureMessage, verificationNotice } from '../../services/EmailVerificationService';

import { getAuth } from 'firebase/auth';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { beginGoogleRedirect, takeGoogleRedirect, clearGoogleRedirect, usesGoogleRedirect } from '../../services/GoogleRedirectService';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
const apiUrl = process.env.REACT_APP_API_URL;
const apiVersion = process.env.REACT_APP_DEFAULT_API_VERSION;

export const SIGNUP_CONFIRMED_ACTION = '[signup action] confirmed signup';
export const SIGNUP_FAILED_ACTION = '[signup action] failed signup';
export const EMAIL_VERIFICATION_REQUIRED = 'EMAIL_VERIFICATION_REQUIRED';
export const LOGIN_CONFIRMED_ACTION = '[login action] confirmed login';
export const COMPANY_SELECTED_ACTION = '[company action] company selected';
export const CREATE_NEW_COMPANY_ACTION = '[company action] create new company';
export const LOGIN_FAILED_ACTION = '[login action] failed login';
export const LOADING_TOGGLE_ACTION = '[Loading action] toggle loading';
export const LOGOUT_ACTION = '[Logout action] logout action';

export const PASSWORD_RESET_REQUEST = '[password reset] request sent';
export const PASSWORD_RESET_SUCCESS = '[password reset] success';
export const PASSWORD_RESET_FAILED = '[password reset] failed';

export const SET_ACCESS_TOKEN = 'SET_ACCESS_TOKEN';
export const SET_USER_DATA = 'SET_USER_DATA';
export const SET_SELECTED_COMPANY_ID = 'SET_SELECTED_COMPANY_ID';
export const SELECT_COMPANY = 'SELECT_COMPANY';
export const USER_AVATAR_UPDATED = 'USER_AVATAR_UPDATED';
export const USER_PROFILE_UPDATED = 'USER_PROFILE_UPDATED';
export const USER_COMPANY_REMOVED = 'USER_COMPANY_REMOVED';

export const setUserAvatar = (avatarUrl) => ({
  type: USER_AVATAR_UPDATED,
  payload: avatarUrl,
});

export const setUserProfile = ({ firstName, lastName, phoneNumber }) => ({
  type: USER_PROFILE_UPDATED,
  payload: { firstName, lastName, phoneNumber },
});

export const removeUserCompany = (companyId) => ({
  type: USER_COMPANY_REMOVED,
  payload: companyId,
});

// New action types for enhanced account linking
export const GOOGLE_ONLY_USER_DETECTED = 'GOOGLE_ONLY_USER_DETECTED';
export const ACCOUNT_METHODS_CHECKED = 'ACCOUNT_METHODS_CHECKED';
export const TRIGGER_GOOGLE_SIGNIN = 'TRIGGER_GOOGLE_SIGNIN';
export const CLEAR_AUTH_MESSAGES = 'CLEAR_AUTH_MESSAGES';

// Production-grade rate limiting and retry logic
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

async function withRetry(operation, maxRetries = MAX_RETRIES) {
  let lastError;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      // Don't retry for certain error types
      const nonRetryableErrors = [
        'SESSION_CHANGED',
        'auth/user-not-found',
        'auth/invalid-email',
        'auth/weak-password',
        'auth/google-only-user',
        'auth/no-password-method',
        'auth/invalid-credential',
        'auth/wrong-password'
      ];

      if (nonRetryableErrors.includes(error.code) || (error.response?.status >= 400 && error.response.status < 500)) {
        throw error;
      }

      // Don't retry on last attempt
      if (attempt === maxRetries) {
        throw error;
      }

      // Exponential backoff
      await delay(RETRY_DELAY * Math.pow(2, attempt - 1));
    }
  }

  throw lastError;
}

// Do not probe account existence or attached providers before recovery.
export function sendPasswordResetAction(email) {
  return async dispatch => {
    dispatch(loadingToggleAction(true));
    try {
      await sendPasswordReset(email);
      dispatch({ type: PASSWORD_RESET_SUCCESS, payload: {
        message: 'Din begäran har tagits emot. Om adressen kan användas för återställning får du en länk via e-post.', email
      } });
      return { success: true };
    } catch (error) {
      const message = error.code === 'auth/too-many-requests'
        ? 'För många försök. Vänta en stund och försök igen.'
        : error.code === 'auth/invalid-email' ? 'Ogiltig e-postadress'
        : 'Kunde inte begära återställning. Försök igen om en stund.';
      dispatch({ type: PASSWORD_RESET_FAILED, payload: { message, code: error.code } });
      return { success: false, message };
    } finally { dispatch(loadingToggleAction(false)); }
  };
}

export function confirmPasswordResetAction(code, newPassword, history) {
  return async (dispatch) => {
    try {
      dispatch(loadingToggleAction(true));

      await confirmPasswordResetWithCode(code, newPassword);
      await clearBrowserIdentity().catch(() => {});
      dispatch({ type: LOGOUT_ACTION });

      dispatch({
        type: PASSWORD_RESET_SUCCESS,
        payload: { message: 'Lösenordet har återställts framgångsrikt' }
      });

      // Show success message before redirect
      setTimeout(() => {
        history.push('/login?message=password-reset-success');
      }, 1500);

    } catch (error) {

      let errorMessage = 'Ett fel inträffade vid återställning av lösenord';

      if (error.code) {
        switch (error.code) {
          case 'auth/weak-password':
            errorMessage = 'Lösenordet är för svagt. Använd minst 8 tecken med stora och små bokstäver samt siffror';
            break;
          case 'auth/invalid-action-code':
            errorMessage = 'Återställningslänken är ogiltig eller felaktig';
            break;
          case 'auth/expired-action-code':
            errorMessage = 'Återställningslänken har gått ut. Begär en ny återställningslänk';
            break;
          case 'auth/network-request-failed':
            errorMessage = 'Nätverksfel. Kontrollera din internetanslutning och försök igen';
            break;
          default:
            errorMessage = error.message || errorMessage;
        }
      }

      dispatch({
        type: PASSWORD_RESET_FAILED,
        payload: { message: errorMessage }
      });

      formatError(error);
    } finally {
      dispatch(loadingToggleAction(false));
    }
  };
}

// Enhanced signup action with account linking detection
export function signupAction(firstName, lastName, phoneNumber, email, password, history, redirectUrl) {
  redirectUrl = safeAuthRedirect(redirectUrl);
  return async (dispatch) => {

    try {
      dispatch(loadingToggleAction(true));

      // Input sanitization
      const sanitizedData = {
        firstName: firstName?.trim()?.substring(0, 50) || '',
        lastName: lastName?.trim()?.substring(0, 50) || '',
        phoneNumber: phoneNumber?.trim()?.substring(0, 20) || '',
        email: email?.trim()?.toLowerCase()?.substring(0, 254) || '',
        password: password || ''
      };

      const response = await signUp(sanitizedData.email, sanitizedData.password);
      const idToken = await response.user.getIdToken();
      await withRetry(() => axios.post(`${apiBaseUrl}/${apiUrl}/${apiVersion}/auth/users`, {
        idToken,
        firstName: sanitizedData.firstName,
        lastName: sanitizedData.lastName,
        phoneNumber: sanitizedData.phoneNumber,
      }, { timeout: 10000 }));

      // A saved profile does not grant a session until email is verified.
      const authData = await doLLSignIn(response, redirectUrl);

      dispatch(confirmedSignupAction({
        email: response.user.email,
        googleUid: response.user.uid,
        uid: authData.userId,
        firstName: sanitizedData.firstName,
        lastName: sanitizedData.lastName,
      }));

      dispatch(setAccessToken(authData.accessToken));

      // Navigate to the appropriate page after signup
      if (redirectUrl === '/') {
        history.push('/select-company');
      } else {
        history.push(redirectUrl);
      }

    } catch (error) {
      // Keep the Firebase identity when a request fails. The user can sign in
      // again to finish onboarding; deleting it here could orphan a saved profile.
      if (error.code === 'auth/email-not-verified') {
        dispatch(signupFailedAction(error.message));
        dispatch({ type: EMAIL_VERIFICATION_REQUIRED, payload: error.verification });
        return;
      }
      let errorMessage = 'Ett fel inträffade vid registrering';

      // Display safe backend validation messages
      if (error.response?.status === 429) {
        errorMessage = 'För många försök. Vänta en stund och försök igen.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.code) {
        switch (error.code) {
          case 'auth/email-already-in-use':
            errorMessage = 'E-postadressen används redan. Logga in istället.';
            break;
          case 'auth/weak-password':
            errorMessage = 'Lösenordet är för svagt. Använd minst 6 tecken.';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Ogiltig e-postadress.';
            break;
          case 'auth/account-exists-different-credential':
            errorMessage = 'Ett konto med denna e-postadress finns redan med en annan inloggningsmetod.';
            break;
          case 'auth/network-request-failed':
            errorMessage = 'Nätverksfel. Kontrollera din internetanslutning och försök igen';
            break;
          default:
            errorMessage = error.message || errorMessage;
        }
      }

      dispatch(signupFailedAction(errorMessage));
      formatError(error);
    } finally {
      dispatch(loadingToggleAction(false));
    }
  };
}

export const setAccessToken = (accessToken) => ({
  type: SET_ACCESS_TOKEN,
  payload: accessToken,
});

export const setUserData = (userData) => ({
  type: SET_USER_DATA,
  payload: userData,
});

export function logout(history, { everywhere = false } = {}) {
  return async (dispatch, getState) => {
    const accessToken = getState?.()?.auth?.accessToken;
    // Invalidate any pending refresh before waiting for the cross-tab lock.
    markBrowserSession('signed_out');
    dispatch({ type: LOGOUT_ACTION });
    let failure;
    try {
      await withBrowserSessionLock(() => axios.post(
        `${apiBaseUrl}/${apiUrl}/${apiVersion}/auth/${everywhere ? 'logout-all' : 'logout'}`,
        {}, { withCredentials: true, timeout: 15000,
          ...(accessToken && { headers: { Authorization: `Bearer ${accessToken}` } }) }
      ));
    } catch (error) { failure = error; }
    finally {
      await clearBrowserIdentity({ notify: false }).catch(() => {});
      dispatch({ type: LOGOUT_ACTION });
      if (history?.push) history.push('/login');
    }
    // Callers can report a failed server revocation without retaining local identity.
    if (failure && !everywhere && history?.push) {
      swal('Du är utloggad här, men servern kunde inte bekräfta utloggningen. Logga in och försök igen när anslutningen fungerar.', { icon: 'error' });
    }
    return { success: !failure, error: failure };
  };
}

async function doLLSignIn(signInData, redirectUrl = '/', assertCurrent = () => {}) {
  const redirect = safeAuthRedirect(redirectUrl);
  rememberInvitation(redirect);
  try {
    // Sign-in already supplies a fresh token; Firebase refreshes it if needed.
    const idToken = await signInData.user.getIdToken();
    const response = await withBrowserSessionLock(async () => {
      assertCurrent();
      const response = await withRetry(() => {
        assertCurrent();
        return axios.post(`${apiBaseUrl}/${apiUrl}/${apiVersion}/auth/google-signin`, { idToken }, {
          withCredentials: true,
          timeout: 10000
        });
      });
      assertCurrent();
      markBrowserSession('signed_in');
      return response;
    });

    return {
      accessToken: response.data.accessToken,
      firstName: response.data.firstname,
      lastName: response.data.lastname,
      userId: response.data.userId,
      avatarUrl: response.data.avatarUrl || null
    };
  } catch (error) {
    if (error.response?.data?.code === 'EMAIL_NOT_VERIFIED') {
      let message, failed = false, retryAfter = 60;
      try {
        message = verificationNotice(await requestEmailVerification(signInData.user, redirect));
      } catch (deliveryError) {
        failed = true;
        retryAfter = Number(deliveryError.response?.data?.retryAfter) || 60;
        message = verificationFailureMessage(deliveryError);
      }
      const verificationError = new Error(message);
      verificationError.code = 'auth/email-not-verified';
      verificationError.verification = { email: signInData.user.email, message, failed, retryAfter };
      throw verificationError;
    }
    if (error.response?.status === 429) {
      throw Object.assign(new Error('För många inloggningsförsök. Vänta en stund och försök igen.'), { code: 'auth/too-many-requests' });
    }
    throw error;
  }
}

// Separate function to fetch companies after authentication
async function fetchUserCompanies(googleId, accessToken) {
  try {
    const companiesResponse = await withRetry(() =>
      axios.get(`${apiBaseUrl}/${apiUrl}/${apiVersion}/auth/users/companies?googleId=${googleId}`, {
        withCredentials: true,
        timeout: 10000,
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      })
    );


    // Convert companies array to object for easy access
    const companiesObject = {};
    // Handle both wrapped response { companies: [...] } and direct array [...]
    const companiesArray = companiesResponse.data.companies || companiesResponse.data;
    if (companiesArray && Array.isArray(companiesArray)) {
      companiesArray.forEach(company => {
        companiesObject[company.company_id] = {
          companyName: company.company_name,
          companyRegNo: company.company_reg_no || '',
          companyType: company.company_type || '',
          vatStatus: company.vat_status || '',
          registrationNumber: company.registration_number || '',
          roles: company.roles || []
        };
      });
    }

    return companiesObject;
  } catch (error) {
    throw error;
  }
}

// Enhanced login action with better error handling and smart detection
export function loginAction(email, password, history, url) {
  url = safeAuthRedirect(url);
  return async (dispatch) => {
    try {
      dispatch(loadingToggleAction(true));

      // Input sanitization
      const sanitizedEmail = email?.trim()?.toLowerCase()?.substring(0, 254) || '';
      const sanitizedPassword = password || '';

      const response = await withRetry(() => login(sanitizedEmail, sanitizedPassword));

      const data = await doLLSignIn(response, url);

      dispatch(loginConfirmedAction({
        email: response.user.email,
        googleUid: response.user.uid,
        uid: data.userId,
        firstName: data.firstName,
        lastName: data.lastName
      }));


      // Set access token in Redux state
      dispatch(setAccessToken(data.accessToken));


      // Fetch companies after setting access token, passing token directly
      const companies = await fetchUserCompanies(response.user.uid, data.accessToken);


      // Set user data with companies
      dispatch(setUserData({
        user: {
          user_id: data.userId,
          first_name: data.firstName,
          last_name: data.lastName,
          email: response.user.email,
          google_id: response.user.uid,
          phone_number: '', // Will be updated from user profile later
          photo_url: response.user.photoURL || null,
          avatar_url: data.avatarUrl || null
        },
        companies: companies
      }));

      if (url === '/') {
        history.push('/select-company');
      } else {
        history.push(url);
      }

    } catch (error) {

      if (error.code === 'auth/email-not-verified') {
        dispatch(loginFailedAction(error.message));
        dispatch({ type: EMAIL_VERIFICATION_REQUIRED, payload: error.verification });
        return;
      }

      let errorMessage = 'Ett fel inträffade vid inloggning';

      // Handle enhanced error messages from our AuthService
      if (error.code) {
        switch (error.code) {
          case 'auth/google-only-user':
            errorMessage = error.message;
            // Dispatch special action for Google-only users
            dispatch({
              type: GOOGLE_ONLY_USER_DETECTED,
              payload: {
                email: email?.trim()?.toLowerCase(),
                canTriggerGoogle: true,
                message: error.message
              }
            });
            break;
          case 'auth/invalid-credential':
          case 'auth/wrong-password':
            errorMessage = 'E-post eller lösenord är felaktigt';
            break;
          case 'auth/user-not-found':
            errorMessage = 'E-post eller lösenord är felaktigt';
            break;
          case 'auth/too-many-requests':
            errorMessage = 'För många inloggningsförsök. Vänta en stund och försök igen';
            break;
          case 'auth/user-disabled':
            errorMessage = 'Kontot har inaktiverats. Kontakta support';
            break;
          case 'auth/network-request-failed':
            errorMessage = 'Nätverksfel. Kontrollera din internetanslutning och försök igen';
            break;
          default:
            errorMessage = error.message || errorMessage;
        }
      }

      dispatch(loginFailedAction(errorMessage));

      // Only call formatError for non-Google-only errors to avoid double messaging
      if (error.code !== 'auth/google-only-user') {
        formatError(error);
      }

    } finally {
      dispatch(loadingToggleAction(false));
    }
  };
}

// Firebase still supplies the verified ID token; only the browser journey changes.
async function completeGoogleLogin(result, redirectUrl, history, dispatch, assertCurrent = () => {}) {
  const data = await doLLSignIn(result, redirectUrl, assertCurrent);
  const marker = sessionMarker();
  dispatch(loginConfirmedAction({
    email: result.user.email, googleUid: result.user.uid, uid: data.userId,
    firstName: data.firstName, lastName: data.lastName
  }));
  dispatch(setAccessToken(data.accessToken));
  const companies = await fetchUserCompanies(result.user.uid, data.accessToken);
  if (marker !== sessionMarker()) throw sessionChangedError();
  dispatch(setUserData({
    user: {
      user_id: data.userId, first_name: data.firstName, last_name: data.lastName,
      email: result.user.email, google_id: result.user.uid, phone_number: '',
      photo_url: result.user.photoURL || null, avatar_url: data.avatarUrl || null
    },
    companies
  }));
  history.replace(redirectUrl === '/' ? '/select-company' : redirectUrl);
}

function googleLoginFailed(error, dispatch) {
  if (error.code === 'auth/email-not-verified') {
    dispatch(loginFailedAction(error.message));
    dispatch({ type: EMAIL_VERIFICATION_REQUIRED, payload: error.verification });
    return;
  }
  // Never put provider URLs, OAuth state or raw SDK errors in the UI/logs.
  const messages = {
    'auth/popup-closed-by-user': 'Inloggning avbröts. Försök igen när du är redo.',
    'auth/popup-blocked': 'Popup blockerades av webbläsaren. Tillåt popup-fönster och försök igen.',
    'auth/cancelled-popup-request': 'Inloggning avbröts. Försök igen när du är redo.',
    'auth/redirect-cancelled': 'Inloggning avbröts eller kunde inte slutföras. Försök igen.',
    'auth/redirect-expired': 'Inloggningsförsöket har gått ut. Försök igen.',
    'auth/web-storage-unsupported': 'Webbläsaren behöver tillåta lagring för att logga in. Kontrollera inställningarna och försök igen.',
    'auth/too-many-requests': 'För många inloggningsförsök. Vänta en stund och försök igen.',
    'auth/account-exists-with-different-credential': 'Ett konto finns redan med samma e-postadress men olika inloggningsmetod.',
    'auth/network-request-failed': 'Nätverksfel. Kontrollera din internetanslutning.',
    SESSION_CHANGED: 'Inloggningen ändrades i en annan flik. Försök igen.'
  };
  dispatch(loginFailedAction(messages[error.code] || 'Social login misslyckades. Försök igen.'));
}

export function socialLoginAction(provider, history, redirectUrl) {
  redirectUrl = safeAuthRedirect(redirectUrl);
  return async dispatch => {
    dispatch(loadingToggleAction(true));
    try {
      if (provider !== 'google') throw new Error('Unsupported provider');
      if (usesGoogleRedirect()) {
        await beginGoogleRedirect(redirectUrl);
      } else {
        // Firebase's helpers require HTTPS. Keep plain-HTTP localhost usable;
        // start:https exercises the production redirect flow locally.
        const result = await signInWithPopup(getAuth(), new GoogleAuthProvider());
        await completeGoogleLogin(result, redirectUrl, history, dispatch);
      }
    } catch (error) {
      clearGoogleRedirect();
      googleLoginFailed(error, dispatch);
    } finally {
      dispatch(loadingToggleAction(false));
    }
  };
}

let redirectCompletion;
export function completeGoogleRedirectAction(history) {
  return dispatch => {
    if (redirectCompletion) return redirectCompletion;
    redirectCompletion = (async () => {
      let pending;
      try {
        pending = await takeGoogleRedirect();
        if (!pending) return false;
        dispatch(loadingToggleAction(true));
        const assertCurrent = () => {
          if (pending.marker !== sessionMarker()) throw sessionChangedError();
        };
        assertCurrent();
        await completeGoogleLogin(pending.result, pending.redirect, history, dispatch, assertCurrent);
      } catch (error) {
        // A failed return is handled here, before normal boot can restore an
        // unrelated old cookie session or discard unverified Firebase proof.
        dispatch({ type: LOGOUT_ACTION });
        const redirect = safeAuthRedirect(pending?.redirect || error.redirect);
        history.replace(redirect === '/' ? '/login' : `/login?redirect=${encodeURIComponent(redirect)}`);
        googleLoginFailed(error, dispatch);
      } finally {
        dispatch(loadingToggleAction(false));
      }
      return true;
    })().finally(() => { redirectCompletion = null; });
    return redirectCompletion;
  };
}

// Company management actions
export function selectCompany(companyId, companyName, companyRegNumber, history, extras = {}) {
  return (dispatch, getState) => {
    // Pull the catalog-side fields from the companies map populated at login
    // when callers don't pass them in explicitly. Redux Thunk always supplies
    // getState — tests must mock it.
    const fromMap = getState()?.auth?.user?.companies?.[companyId] || {};
    const companyType = extras.companyType || fromMap.companyType || '';
    const vatStatus = extras.vatStatus || fromMap.vatStatus || '';
    const registrationNumber = extras.registrationNumber || fromMap.registrationNumber || '';

    localStorage.setItem('selectedCompany', JSON.stringify({
      companyId, companyName, companyRegNumber, companyType, vatStatus, registrationNumber,
    }));

    dispatch(companySelectedAction(
      companyId, companyName, companyRegNumber,
      { companyType, vatStatus, registrationNumber }
    ));
    history.push(`/${companyId}/dashboard`);
  };
}

export function setPreSelectedCompany(companyId, companyName, extras = {}) {
  return (dispatch, getState) => {
    const fromMap = getState()?.auth?.user?.companies?.[companyId] || {};
    const companyType = extras.companyType || fromMap.companyType || '';
    const vatStatus = extras.vatStatus || fromMap.vatStatus || '';
    const registrationNumber = extras.registrationNumber || fromMap.registrationNumber || '';
    dispatch(companySelectedAction(
      companyId, companyName, undefined,
      { companyType, vatStatus, registrationNumber }
    ));
  };
}

export function createNewCompany(history) {
  return (dispatch) => {
    dispatch(createNewCompanyAction());
    history.push('/create-company');
  };
}

// Basic action creators
export function loginFailedAction(data) {
  return {
    type: LOGIN_FAILED_ACTION,
    payload: data,
  };
}

export function loginConfirmedAction(data) {
  return {
    type: LOGIN_CONFIRMED_ACTION,
    payload: {
      data
    },
  };
}

export function confirmedSignupAction(payload) {
  return {
    type: SIGNUP_CONFIRMED_ACTION,
    payload: {
      data: payload,
    },
  };
}

export function signupFailedAction(message) {
  return {
    type: SIGNUP_FAILED_ACTION,
    payload: message,
  };
}

export function loadingToggleAction(status) {
  return {
    type: LOADING_TOGGLE_ACTION,
    payload: status,
  };
}

export function companySelectedAction(companyId, companyName, companyRegNumber, extras = {}) {
  return {
    type: COMPANY_SELECTED_ACTION,
    payload: {
      companyId,
      companyName,
      companyRegNumber,
      companyType: extras.companyType || '',
      vatStatus: extras.vatStatus || '',
      registrationNumber: extras.registrationNumber || '',
    },
  };
}

export function createNewCompanyAction(newCompany) {
  return {
    type: CREATE_NEW_COMPANY_ACTION,
    payload: newCompany,
  };
}

// Enhanced action creators for account linking
export function triggerGoogleSignInAction() {
  return {
    type: TRIGGER_GOOGLE_SIGNIN,
    payload: { shouldTriggerGoogle: true }
  };
}

export function checkAccountMethodsAction(email) {
  return async (dispatch) => {
    try {
      const methods = await withRetry(() => checkUserSignInMethods(email));

      dispatch({
        type: ACCOUNT_METHODS_CHECKED,
        payload: {
          email,
          methods: methods.methods,
          hasPassword: methods.hasPassword,
          hasGoogle: methods.hasGoogle
        }
      });

      return methods;
    } catch (error) {
      return { hasPassword: false, hasGoogle: false, methods: [], error: true };
    }
  };
}

export function clearAuthMessages() {
  return {
    type: CLEAR_AUTH_MESSAGES,
    payload: {}
  };
}



// import {
//   formatError,
//   login,
//   runLogoutTimer,
//   saveTokenInLocalStorage,
//   signUp,
//   sendPasswordReset,
//   verifyResetCode,
//   confirmPasswordResetWithCode,
//   checkUserSignInMethods,
// } from '../../services/AuthService';

// import axios from 'axios';

// import { getAuth } from 'firebase/auth';
// import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
// import { beginGoogleRedirect, takeGoogleRedirect, clearGoogleRedirect, usesGoogleRedirect } from '../../services/GoogleRedirectService';

// const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
// const apiUrl = process.env.REACT_APP_API_URL;
// const apiVersion = process.env.REACT_APP_DEFAULT_API_VERSION;

// export const SIGNUP_CONFIRMED_ACTION = '[signup action] confirmed signup';
// export const SIGNUP_FAILED_ACTION = '[signup action] failed signup';
// export const LOGIN_CONFIRMED_ACTION = '[login action] confirmed login';
// export const COMPANY_SELECTED_ACTION = '[company action] company selected';
// export const CREATE_NEW_COMPANY_ACTION = '[company action] create new company';
// export const LOGIN_FAILED_ACTION = '[login action] failed login';
// export const LOADING_TOGGLE_ACTION = '[Loading action] toggle loading';
// export const LOGOUT_ACTION = '[Logout action] logout action';

// export const PASSWORD_RESET_REQUEST = '[password reset] request sent';
// export const PASSWORD_RESET_SUCCESS = '[password reset] success';
// export const PASSWORD_RESET_FAILED = '[password reset] failed';

// export const SET_ACCESS_TOKEN = 'SET_ACCESS_TOKEN';
// export const SET_USER_DATA = 'SET_USER_DATA';
// export const SET_SELECTED_COMPANY_ID = 'SET_SELECTED_COMPANY_ID';

// // Enhanced password reset action with smart account detection
// export function sendPasswordResetAction(email) {
//   return async (dispatch) => {
//     try {
//       dispatch(loadingToggleAction(true));

//       // Enhanced: Check account type before attempting password reset
//       const signInMethods = await checkUserSignInMethods(email);

//       if (signInMethods.isEmpty && !signInMethods.error) {
//         throw {
//           code: 'auth/user-not-found',
//           message: 'Ingen användare hittades med den e-postadressen'
//         };
//       }

//       if (signInMethods.hasGoogle && !signInMethods.hasPassword) {
//         throw {
//           code: 'auth/google-only-user',
//           message: 'Detta konto använder Google för inloggning. Använd "Fortsätt med Google" istället.',
//           canAddPassword: true,
//           availableMethods: signInMethods.methods
//         };
//       }

//       if (!signInMethods.hasPassword && signInMethods.methods.length > 0) {
//         throw {
//           code: 'auth/no-password-method',
//           message: 'Detta konto har inget lösenord. Kontakta support för hjälp.',
//           availableMethods: signInMethods.methods
//         };
//       }

//       // Proceed with password reset for accounts that have passwords
//       await sendPasswordReset(email);

//       dispatch({
//         type: PASSWORD_RESET_SUCCESS,
//         payload: {
//           message: 'Återställningslänk skickad till din e-post',
//           email: email
//         }
//       });

//     } catch (error) {
//       console.error('Password reset error:', error);

//       let errorMessage = 'Ett fel inträffade vid skickande av återställningslänk';

//       // Enhanced error handling based on our AuthService
//       if (error.code) {
//         switch (error.code) {
//           case 'auth/user-not-found':
//             errorMessage = 'Ingen användare hittades med den e-postadressen';
//             break;
//           case 'auth/invalid-email':
//             errorMessage = 'Ogiltig e-postadress';
//             break;
//           case 'auth/too-many-requests':
//             errorMessage = 'För många försök. Vänta en stund och försök igen';
//             break;
//           case 'auth/google-only-user':
//             errorMessage = error.message;
//             break;
//           case 'auth/no-password-method':
//             errorMessage = error.message;
//             break;
//           default:
//             errorMessage = error.message || errorMessage;
//         }
//       }

//       dispatch({
//         type: PASSWORD_RESET_FAILED,
//         payload: {
//           message: errorMessage,
//           code: error.code,
//           availableMethods: error.availableMethods || [],
//           canAddPassword: error.canAddPassword || false
//         }
//       });

//       // Only show formatError for actual Firebase errors, not our custom ones
//       if (!error.code || !error.code.startsWith('auth/google-only-user') && !error.code.startsWith('auth/no-password-method')) {
//         formatError(error);
//       }

//     } finally {
//       dispatch(loadingToggleAction(false));
//     }
//   };
// }

// export function confirmPasswordResetAction(code, newPassword, history) {
//   return async (dispatch) => {
//     try {
//       dispatch(loadingToggleAction(true));

//       await confirmPasswordResetWithCode(code, newPassword);

//       dispatch({
//         type: PASSWORD_RESET_SUCCESS,
//         payload: { message: 'Lösenordet har återställts framgångsrikt' }
//       });

//       // Show success message before redirect
//       setTimeout(() => {
//         history.push('/login?message=password-reset-success');
//       }, 1500);

//     } catch (error) {
//       console.error('Password reset confirmation error:', error);

//       let errorMessage = 'Ett fel inträffade vid återställning av lösenord';

//       if (error.code) {
//         switch (error.code) {
//           case 'auth/weak-password':
//             errorMessage = 'Lösenordet är för svagt. Använd minst 8 tecken med stora och små bokstäver samt siffror';
//             break;
//           case 'auth/invalid-action-code':
//             errorMessage = 'Återställningslänken är ogiltig eller felaktig';
//             break;
//           case 'auth/expired-action-code':
//             errorMessage = 'Återställningslänken har gått ut. Begär en ny återställningslänk';
//             break;
//           default:
//             errorMessage = error.message || errorMessage;
//         }
//       }

//       dispatch({
//         type: PASSWORD_RESET_FAILED,
//         payload: { message: errorMessage }
//       });

//       formatError(error);
//     } finally {
//       dispatch(loadingToggleAction(false));
//     }
//   };
// }

// // Enhanced signup action with account linking detection
// export function signupAction(firstName, lastName, phoneNumber, email, password, history, redirectUrl) {
//   return async (dispatch) => {
//     try {
//       dispatch(loadingToggleAction(true));

//       // Check if account already exists before attempting signup
//       const existingMethods = await checkUserSignInMethods(email);

//       if (existingMethods.methods.length > 0 && !existingMethods.error) {
//         let errorMessage = 'Ett konto med denna e-postadress finns redan.';

//         if (existingMethods.hasGoogle && !existingMethods.hasPassword) {
//           errorMessage += ' Logga in med "Fortsätt med Google" istället.';
//         } else if (existingMethods.hasPassword) {
//           errorMessage += ' Logga in istället.';
//         }

//         dispatch(signupFailedAction(errorMessage));
//         return;
//       }

//       const response = await signUp(email, password);
//       console.log("Signup response: ", response);
//       saveTokenInLocalStorage(response);

//       // Using axios make call to backend api to store user in database
//       const backendResponse = await axios.post(`${apiBaseUrl}/${apiUrl}/${apiVersion}/auth/users`, {
//         email: response.user.email,
//         googleId: response.user.uid,
//         firstName: firstName,
//         lastName: lastName,
//         phoneNumber: phoneNumber,
//       });

//       console.log("User added:", backendResponse);

//       const llUid = backendResponse.data.result.userId;

//       dispatch(confirmedSignupAction({
//         email: response.user.email,
//         googleUid: response.user.uid,
//         uid: llUid,
//         firstName: firstName,
//         lastName: lastName,
//       }));

//       dispatch(setAccessToken(backendResponse.data.accessToken));

//       // Navigate to the appropriate page after signup
//       if (redirectUrl === '/') {
//         history.push('/auth/select-company');
//       } else {
//         history.push(redirectUrl);
//       }

//     } catch (error) {
//       console.log("Signup error: ", error);

//       let errorMessage = 'Ett fel inträffade vid registrering';

//       if (error.code) {
//         switch (error.code) {
//           case 'auth/email-already-in-use':
//             errorMessage = 'E-postadressen används redan. Logga in istället.';
//             break;
//           case 'auth/weak-password':
//             errorMessage = 'Lösenordet är för svagt. Använd minst 6 tecken.';
//             break;
//           case 'auth/invalid-email':
//             errorMessage = 'Ogiltig e-postadress.';
//             break;
//           case 'auth/account-exists-different-credential':
//             errorMessage = 'Ett konto med denna e-postadress finns redan med en annan inloggningsmetod.';
//             break;
//           default:
//             errorMessage = error.message || errorMessage;
//         }
//       }

//       dispatch(signupFailedAction(errorMessage));
//       formatError(error);
//     } finally {
//       dispatch(loadingToggleAction(false));
//     }
//   };
// }

// export const setAccessToken = (accessToken) => ({
//   type: SET_ACCESS_TOKEN,
//   payload: accessToken,
// });

// export const setUserData = (userData) => ({
//   type: SET_USER_DATA,
//   payload: userData,
// });

// export function logout(history) {
//   return (dispatch) => {
//     axios
//       .post(
//         `${apiBaseUrl}/${apiUrl}/${apiVersion}/auth/logout`,
//         {},
//         { withCredentials: true }
//       )
//       .then((response) => {
//         console.log('Logout response:', response);

//         // Clear any local storage or state related to the user
//         localStorage.removeItem('selectedCompany');
//         localStorage.removeItem('userDetails');

//         // Dispatch logout action to update Redux state
//         dispatch({ type: LOGOUT_ACTION });

//         // Redirect to login page after successful logout
//         history.push('/login');
//       })
//       .catch((error) => {
//         console.log('Logout error:', error);

//         // Handle errors if necessary - still clear local state
//         localStorage.removeItem('selectedCompany');
//         localStorage.removeItem('userDetails');
//         dispatch({ type: LOGOUT_ACTION });
//         history.push('/login');
//       });
//   };
// }

// async function doLLSignIn(googelSignInData) {
//   try {
//     const data = {
//       name: googelSignInData.user.displayName,
//       email: googelSignInData.user.email,
//       userGoogleId: googelSignInData.user.uid
//     };

//     const response = await axios.post(`${apiBaseUrl}/${apiUrl}/${apiVersion}/auth/google-signin`, data, { withCredentials: true });
//     console.log("LedgerLoop sign in:", response.data);

//     return {
//       accessToken: response.data.accessToken,
//       firstName: response.data.firstname,
//       lastName: response.data.lastname,
//       userId: response.data.userId,
//     };

//   } catch (error) {
//     console.error(`Error: ${error}`);
//     throw error;
//   }
// }

// // Enhanced login action with better error handling
// export function loginAction(email, password, history, url) {
//   return async (dispatch) => {
//     try {
//       dispatch(loadingToggleAction(true));

//       const response = await login(email, password);
//       console.log("Login response: ", response);

//       const data = await doLLSignIn(response);
//       console.log("Data: ", data);

//       dispatch(loginConfirmedAction({
//         email: response.user.email,
//         googleUid: response.user.uid,
//         uid: data.userId,
//         firstName: data.firstName,
//         lastName: data.lastName
//       }));
//       dispatch(setAccessToken(data.accessToken));

//       if (url === '/') {
//         history.push('/select-company');
//       } else {
//         history.push(url);
//       }

//     } catch (error) {
//       console.error('Login action error:', error);

//       let errorMessage = 'Ett fel inträffade vid inloggning';

//       // Handle enhanced error messages from our AuthService
//       if (error.code) {
//         switch (error.code) {
//           case 'auth/google-only-user':
//             errorMessage = error.message;
//             // Dispatch special action for Google-only users
//             dispatch({
//               type: 'GOOGLE_ONLY_USER_DETECTED',
//               payload: { email, canTriggerGoogle: true }
//             });
//             break;
//           case 'auth/invalid-credential':
//           case 'auth/wrong-password':
//             errorMessage = 'E-post eller lösenord är felaktigt';
//             break;
//           case 'auth/user-not-found':
//             errorMessage = 'Ingen användare hittades med den e-postadressen';
//             break;
//           case 'auth/too-many-requests':
//             errorMessage = 'För många inloggningsförsök. Vänta en stund och försök igen';
//             break;
//           case 'auth/user-disabled':
//             errorMessage = 'Kontot har inaktiverats. Kontakta support';
//             break;
//           default:
//             errorMessage = error.message || errorMessage;
//         }
//       }

//       dispatch(loginFailedAction(errorMessage));

//       // Only call formatError for non-Google-only errors to avoid double messaging
//       if (error.code !== 'auth/google-only-user') {
//         formatError(error);
//       }

//     } finally {
//       dispatch(loadingToggleAction(false));
//     }
//   };
// }

// // Enhanced social login action
// export function socialLoginAction(provider, history, redirectUrl) {
//   console.log("Social login action: ", provider, redirectUrl);

//   return async (dispatch) => {
//     try {
//       dispatch(loadingToggleAction(true));

//       // Initialize the provider based on which one was selected
//       let authProvider;
//       authProvider = new GoogleAuthProvider();

//       // Get Firebase auth instance
//       const auth = getAuth();

//       // Sign in with popup
//       const result = await signInWithPopup(auth, authProvider);
//       console.log("Social login result: ", result);

//       const data = await doLLSignIn(result);

//       dispatch(loginConfirmedAction({
//         email: result.user.email,
//         googleUid: result.user.uid,
//         uid: data.userId,
//         firstName: data.firstName,
//         lastName: data.lastName
//       }));
//       dispatch(setAccessToken(data.accessToken));

//       if (redirectUrl === '/') {
//         history.push('/select-company');
//       } else {
//         history.push(redirectUrl);
//       }

//     } catch (error) {
//       console.error('Social login error:', error);

//       // Handle specific errors
//       let errorMessage = 'Social login misslyckades. Försök igen.';

//       if (error.code) {
//         switch (error.code) {
//           case 'auth/popup-closed-by-user':
//             errorMessage = 'Inloggning avbröts. Popup-fönstret stängdes.';
//             break;
//           case 'auth/popup-blocked':
//             errorMessage = 'Popup blockerades av webbläsaren. Tillåt popup-fönster och försök igen.';
//             break;
//           case 'auth/account-exists-with-different-credential':
//             errorMessage = 'Ett konto finns redan med samma e-postadress men olika inloggningsmetod.';
//             break;
//           case 'auth/cancelled-popup-request':
//             // User cancelled - don't show error
//             return;
//           case 'auth/network-request-failed':
//             errorMessage = 'Nätverksfel. Kontrollera din internetanslutning.';
//             break;
//           default:
//             errorMessage = error.message || errorMessage;
//         }
//       }

//       dispatch({
//         type: LOGIN_FAILED_ACTION,
//         payload: errorMessage,
//       });

//     } finally {
//       dispatch(loadingToggleAction(false));
//     }
//   };
// }

// export function selectCompany(companyId, companyName, companyRegNumber, history) {
//   return (dispatch) => {
//     localStorage.setItem('selectedCompany', JSON.stringify({ companyId, companyName, companyRegNumber }));

//     dispatch(companySelectedAction(companyId, companyName, companyRegNumber));
//     history.push(`/${companyId}/dashboard`);
//   };
// }

// export function setPreSelectedCompany(companyId, companyName) {
//   return (dispatch) => {
//     dispatch(companySelectedAction(companyId, companyName));
//   };
// }

// export function createNewCompany(history) {
//   return (dispatch) => {
//     dispatch(createNewCompanyAction());
//     history.push('/create-company');
//   };
// }

// export function loginFailedAction(data) {
//   return {
//     type: LOGIN_FAILED_ACTION,
//     payload: data,
//   };
// }

// export function loginConfirmedAction(data) {
//   console.log("Login confirmed action: ", data);
//   return {
//     type: LOGIN_CONFIRMED_ACTION,
//     payload: {
//       data
//     },
//   };
// }

// export function confirmedSignupAction(payload) {
//   return {
//     type: SIGNUP_CONFIRMED_ACTION,
//     payload: {
//       data: payload,
//     },
//   };
// }

// export function signupFailedAction(message) {
//   return {
//     type: SIGNUP_FAILED_ACTION,
//     payload: message,
//   };
// }

// export function loadingToggleAction(status) {
//   return {
//     type: LOADING_TOGGLE_ACTION,
//     payload: status,
//   };
// }

// export function companySelectedAction(companyId, companyName, companyRegNumber) {
//   return {
//     type: COMPANY_SELECTED_ACTION,
//     payload: { companyId, companyName, companyRegNumber },
//   };
// }

// export function createNewCompanyAction(newCompany) {
//   return {
//     type: CREATE_NEW_COMPANY_ACTION,
//     payload: newCompany,
//   };
// }

// // New action to handle account linking scenarios
// export function triggerGoogleSignInAction() {
//   return {
//     type: 'TRIGGER_GOOGLE_SIGNIN',
//     payload: { shouldTriggerGoogle: true }
//   };
// }

// // New action for handling account method detection
// export function checkAccountMethodsAction(email) {
//   return async (dispatch) => {
//     try {
//       const methods = await checkUserSignInMethods(email);

//       dispatch({
//         type: 'ACCOUNT_METHODS_CHECKED',
//         payload: {
//           email,
//           methods: methods.methods,
//           hasPassword: methods.hasPassword,
//           hasGoogle: methods.hasGoogle
//         }
//       });

//       return methods;
//     } catch (error) {
//       console.error('Error checking account methods:', error);
//       return { hasPassword: false, hasGoogle: false, methods: [], error: true };
//     }
//   };
// }



// import {
//   formatError,
//   login,
//   runLogoutTimer,
//   saveTokenInLocalStorage,
//   signUp,
//   sendPasswordReset,
//   verifyResetCode,
//   confirmPasswordResetWithCode,
// } from '../../services/AuthService';

// import axios from 'axios';

// import { getAuth } from 'firebase/auth';
// import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
// import { beginGoogleRedirect, takeGoogleRedirect, clearGoogleRedirect, usesGoogleRedirect } from '../../services/GoogleRedirectService';

// const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
// const apiUrl = process.env.REACT_APP_API_URL;
// const apiVersion = process.env.REACT_APP_DEFAULT_API_VERSION;


// export const SIGNUP_CONFIRMED_ACTION = '[signup action] confirmed signup';
// export const SIGNUP_FAILED_ACTION = '[signup action] failed signup';
// export const LOGIN_CONFIRMED_ACTION = '[login action] confirmed login';
// export const COMPANY_SELECTED_ACTION = '[company action] company selected';
// export const CREATE_NEW_COMPANY_ACTION = '[company action] create new company';
// export const LOGIN_FAILED_ACTION = '[login action] failed login';
// export const LOADING_TOGGLE_ACTION = '[Loading action] toggle loading';
// export const LOGOUT_ACTION = '[Logout action] logout action';

// export const PASSWORD_RESET_REQUEST = '[password reset] request sent';
// export const PASSWORD_RESET_SUCCESS = '[password reset] success';
// export const PASSWORD_RESET_FAILED = '[password reset] failed';

// export const SET_ACCESS_TOKEN = 'SET_ACCESS_TOKEN';
// export const SET_USER_DATA = 'SET_USER_DATA';
// export const SET_SELECTED_COMPANY_ID = 'SET_SELECTED_COMPANY_ID';


// // Add password reset actions
// export function sendPasswordResetAction(email) {
//   return async (dispatch) => {
//     try {
//       dispatch(loadingToggleAction(true));

//       await sendPasswordReset(email);

//       dispatch({
//         type: PASSWORD_RESET_SUCCESS,
//         payload: { message: 'Återställningslänk skickad till din e-post' }
//       });

//       // Show success message
//       // You can use swal here or handle in component

//     } catch (error) {
//       console.error('Password reset error:', error);

//       let errorMessage = 'Ett fel inträffade vid skickande av återställningslänk';

//       if (error.code) {
//         switch (error.code) {
//           case 'auth/user-not-found':
//             errorMessage = 'Ingen användare hittades med den e-postadressen';
//             break;
//           case 'auth/invalid-email':
//             errorMessage = 'Ogiltig e-postadress';
//             break;
//           case 'auth/too-many-requests':
//             errorMessage = 'För många försök. Vänta en stund och försök igen';
//             break;
//           default:
//             errorMessage = error.message;
//         }
//       }

//       dispatch({
//         type: PASSWORD_RESET_FAILED,
//         payload: { message: errorMessage }
//       });

//       formatError(error);
//     } finally {
//       dispatch(loadingToggleAction(false));
//     }
//   };
// }

// export function confirmPasswordResetAction(code, newPassword, history) {
//   return async (dispatch) => {
//     try {
//       dispatch(loadingToggleAction(true));

//       await confirmPasswordResetWithCode(code, newPassword);

//       dispatch({
//         type: PASSWORD_RESET_SUCCESS,
//         payload: { message: 'Lösenordet har återställts framgångsrikt' }
//       });

//       // Redirect to login
//       history.push('/login');

//     } catch (error) {
//       console.error('Password reset confirmation error:', error);

//       let errorMessage = 'Ett fel inträffade vid återställning av lösenord';

//       if (error.code) {
//         switch (error.code) {
//           case 'auth/weak-password':
//             errorMessage = 'Lösenordet är för svagt. Använd minst 6 tecken';
//             break;
//           case 'auth/invalid-action-code':
//           case 'auth/expired-action-code':
//             errorMessage = 'Återställningslänken är ogiltig eller har löpt ut';
//             break;
//           default:
//             errorMessage = error.message;
//         }
//       }

//       dispatch({
//         type: PASSWORD_RESET_FAILED,
//         payload: { message: errorMessage }
//       });

//       formatError(error);
//     } finally {
//       dispatch(loadingToggleAction(false));
//     }
//   };
// }


// export function signupAction(firstName, lastName, phoneNumber, email, password, history, redirectUrl) {
//   return (dispatch) => {
//     signUp(email, password)
//       .then((response) => {
//         console.log("Signup response: ", response);
//         saveTokenInLocalStorage(response);

//         //Using axios make call to backend api to store user in database
//         axios.post(`${apiBaseUrl}/${apiUrl}/${apiVersion}/auth/users`, {
//           email: response.user.email,
//           googleId: response.user.uid,
//           firstName: firstName,
//           lastName: lastName,
//           phoneNumber: phoneNumber,
//         })
//           .then((backendResponse) => {
//             console.log("User added:", backendResponse);

//             const llUid = backendResponse.data.result.userId;

//             /*runLogoutTimer(
//               dispatch,
//               response._tokenResponse.expiresIn * 1000,
//               history,
//             );*/

//             console.log("Dispatching confirmedSignupAction");
//             console.log("Redirect URL: ", redirectUrl);
//             console.log("The response: ", response);

//             dispatch(confirmedSignupAction(
//               {
//                 email: response.user.email,
//                 googleUid: response.user.uid,
//                 uid: llUid,
//                 firstName: firstName,
//                 lastName: lastName,
//               }
//             ));

//             dispatch(setAccessToken(backendResponse.data.accessToken));


//             // Navigate to the appropriate page after signup
//             if (redirectUrl === '/') {
//               history.push('/auth/select-company');
//             } else {
//               history.push(redirectUrl);
//             }
//           })
//           .catch((error) => {
//             console.log("Backend API error:", error);
//             // Handle backend API error
//             const errorMessage = formatError(error.response);
//             dispatch(signupFailedAction(errorMessage));
//           });
//       })
//       .catch((error) => {
//         console.log("Signup error: ", error);
//         const errorMessage = formatError(error.response);
//         dispatch(signupFailedAction(errorMessage));
//       });
//   };
// }


// export const setAccessToken = (accessToken) => ({
//   type: SET_ACCESS_TOKEN,
//   payload: accessToken,
// });

// export const setUserData = (userData) => ({
//   type: SET_USER_DATA,
//   payload: userData,
// });


// export function logout(history) {
//   return (dispatch) => {
//     axios
//       .post(
//         `${apiBaseUrl}/${apiUrl}/${apiVersion}/auth/logout`,
//         {},
//         { withCredentials: true }
//       )
//       .then((response) => {
//         console.log('Logout response:', response);

//         // Clear any local storage or state related to the user
//         localStorage.removeItem('selectedCompany');

//         // Dispatch logout action to update Redux state
//         dispatch({ type: LOGOUT_ACTION });

//         // Redirect to login page after successful logout
//         history.push('/login');
//       })
//       .catch((error) => {
//         console.log('Logout error:', error);

//         // Handle errors if necessary
//         // For example, you might still want to redirect even if logout failed
//         dispatch({ type: LOGOUT_ACTION });
//         history.push('/login');
//       });
//   };
// }


// async function doLLSignIn(googelSignInData) {
//   try {
//     const data = {
//       name: googelSignInData.user.displayName,
//       email: googelSignInData.user.email,
//       userGoogleId: googelSignInData.user.uid
//     };

//     const response = await axios.post(`${apiBaseUrl}/${apiUrl}/${apiVersion}/auth/google-signin`, data, { withCredentials: true });
//     console.log("LedgerLop sign in:", response.data);

//     return {
//       accessToken: response.data.accessToken,
//       firstName: response.data.firstname,
//       lastName: response.data.lastname,
//       userId: response.data.userId,
//     };

//   } catch (error) {
//     console.error(`Error: ${error}`);
//   }
// }

// export function loginAction(email, password, history, url) {
//   return (dispatch) => {
//     login(email, password)
//       .then(async (response) => {

//         console.log("Login response: ", response);

//         const data = await doLLSignIn(response);

//         console.log("Data: ", data);

//         dispatch(loginConfirmedAction({
//           email: response.user.email,
//           googleUid: response.user.uid,
//           uid: data.userId,
//           firstName: data.firstName,
//           lastName: data.lastName
//         }));
//         dispatch(setAccessToken(data.accessToken));

//         if (url === '/') {
//           history.push('/select-company'); //Used to be /dashboard
//         } else {
//           history.push(url);
//         }

//       })
//       .catch((error) => {
//         const errorMessage = error.message;
//         formatError(error);
//         dispatch(loginFailedAction(errorMessage));
//       });
//   };
// }

// export function socialLoginAction(provider, history, redirectUrl) {

//   console.log("Social login action: ", provider, redirectUrl);

//   return async (dispatch) => {
//     try {
//       // Initialize the provider based on which one was selected
//       let authProvider;

//       authProvider = new GoogleAuthProvider();

//       // Get Firebase auth instance
//       const auth = getAuth();

//       // Sign in with popup
//       const result = await signInWithPopup(auth, authProvider);

//       console.log("Social login result: ", result);

//       const data = await doLLSignIn(result);

//       dispatch(loginConfirmedAction({
//         email: result.user.email,
//         googleUid: result.user.uid,
//         uid: data.userId,
//         firstName: data.firstName,
//         lastName: data.lastName
//       }));
//       dispatch(setAccessToken(data.accessToken));

//       if (redirectUrl === '/') {
//         history.push('/select-company'); //Used to be /dashboard
//       } else {
//         history.push(redirectUrl);
//       }

//       // Get the user token
//       /*const token = await result.user.getIdToken();

//       // Optionally get additional user info
//       const user = {
//         email: result.user.email,
//         displayName: result.user.displayName,
//         photoURL: result.user.photoURL,
//         uid: result.user.uid,
//         token,
//       };

//       // Save to local storage or state management
//       localStorage.setItem('userDetails', JSON.stringify(user));

//       // Dispatch login confirmed action
//       dispatch({
//         type: LOGIN_CONFIRMED_ACTION,
//         payload: user,
//       });

//       // Redirect user
//       history.push(redirectUrl);
//       */
//     } catch (error) {
//       console.error('Social login error:', error);

//       // Handle specific errors
//       let errorMessage = 'Social login failed. Please try again.';

//       if (error.code) {
//         switch (error.code) {
//           case 'auth/popup-closed-by-user':
//             errorMessage = 'Login canceled. The popup was closed.';
//             break;
//           case 'auth/account-exists-with-different-credential':
//             errorMessage = 'An account already exists with the same email address but different sign-in credentials.';
//             break;
//           default:
//             errorMessage = error.message;
//         }
//       }

//       dispatch({
//         type: LOGIN_FAILED_ACTION,
//         payload: errorMessage,
//       });
//     } finally {
//       dispatch(loadingToggleAction(false));
//     }
//   };
// }

// export function selectCompany(companyId, companyName, companyRegNumber, history) {
//   return (dispatch) => {
//     localStorage.setItem('selectedCompany', JSON.stringify({ companyId, companyName, companyRegNumber }));

//     dispatch(companySelectedAction(companyId, companyName, companyRegNumber));
//     history.push(`/${companyId}/dashboard`);
//   };
// }

// export function setPreSelectedCompany(companyId, companyName) {
//   return (dispatch) => {
//     dispatch(companySelectedAction(companyId, companyName));
//   };
// }

// export function createNewCompany(history) {
//   return (dispatch) => {
//     dispatch(createNewCompanyAction());
//     history.push('/create-company');
//   };
// }

// export function loginFailedAction(data) {
//   return {
//     type: LOGIN_FAILED_ACTION,
//     payload: data,
//   };
// }

// export function loginConfirmedAction(data) {
//   console.log("Login confirmed action: ", data);
//   return {
//     type: LOGIN_CONFIRMED_ACTION,
//     payload: {
//       data
//     },
//   };
// }

// export function confirmedSignupAction(payload) {
//   return {
//     type: SIGNUP_CONFIRMED_ACTION,
//     payload: {
//       data: payload,
//     },
//   };
// }

// export function signupFailedAction(message) {
//   return {
//     type: SIGNUP_FAILED_ACTION,
//     payload: message,
//   };
// }

// export function loadingToggleAction(status) {
//   return {
//     type: LOADING_TOGGLE_ACTION,
//     payload: status,
//   };
// }

// export function companySelectedAction(companyId, companyName, companyRegNumber) {
//   return {
//     type: COMPANY_SELECTED_ACTION,
//     payload: { companyId, companyName, companyRegNumber },
//   };
// }

// export function createNewCompanyAction(newCompany) {
//   return {
//     type: CREATE_NEW_COMPANY_ACTION,
//     payload: newCompany,
//   };
// }
